import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../utils/api";

export type LabPreferenceScreenshot = {
  fileId: string;
  fileName: string;
  size?: number;
  createdAt?: string | null;
};

interface LabPreferenceState {
  labPreferences: any | null;
  screenshots: LabPreferenceScreenshot[];
  loading: boolean;
  screenshotsLoading: boolean;
  uploadingScreenshots: boolean;
  updating: boolean;
  error: string | null;
}

const initialState: LabPreferenceState = {
  labPreferences: null,
  screenshots: [],
  loading: false,
  screenshotsLoading: false,
  uploadingScreenshots: false,
  updating: false,
  error: null,
};

export const fetchLabPreferenceByToken = createAsyncThunk(
  "lab/fetchLabPreferenceByToken",
  async (token: string, { rejectWithValue }) => {
    try {
      const res = await api.get(`/auth/admin/labpreferences/${token}`);
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch lab preference"
      );
    }
  }
);

export const fetchLabPreferenceScreenshots = createAsyncThunk(
  "lab/fetchLabPreferenceScreenshots",
  async (token: string, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/auth/admin/labpreferences/${token}/screenshots`
      );
      return (res.data.data || []) as LabPreferenceScreenshot[];
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch screenshots"
      );
    }
  }
);

export const uploadLabPreferenceScreenshots = createAsyncThunk(
  "lab/uploadLabPreferenceScreenshots",
  async (
    { token, files }: { token: string; files: File[] },
    { rejectWithValue }
  ) => {
    try {
      const form = new FormData();
      files.forEach((file) => form.append("screenshots", file));
      const res = await api.post(
        `/auth/admin/labpreferences/${token}/screenshots`,
        form,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return (res.data.data?.screenshots || []) as LabPreferenceScreenshot[];
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to upload screenshots"
      );
    }
  }
);

export const updateLabPreference = createAsyncThunk(
  "lab/updateLabPreference",
  async (
    { token, lab_preferences }: { token: string; lab_preferences: any },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.post(`/auth/admin/update/labpreference`, {
        token,
        lab_preferences,
      });
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to update lab preference"
      );
    }
  }
);

const labDetailsSlice = createSlice({
  name: "labPreference",
  initialState,
  reducers: {
    resetLabPreferenceState(state) {
      state.labPreferences = null;
      state.screenshots = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLabPreferenceByToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLabPreferenceByToken.fulfilled, (state, action) => {
        state.labPreferences = action.payload;
        state.loading = false;
      })
      .addCase(fetchLabPreferenceByToken.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      })
      .addCase(fetchLabPreferenceScreenshots.pending, (state) => {
        state.screenshotsLoading = true;
      })
      .addCase(fetchLabPreferenceScreenshots.fulfilled, (state, action) => {
        state.screenshotsLoading = false;
        state.screenshots = action.payload;
      })
      .addCase(fetchLabPreferenceScreenshots.rejected, (state, action) => {
        state.screenshotsLoading = false;
        state.error = action.payload as string;
      })
      .addCase(uploadLabPreferenceScreenshots.pending, (state) => {
        state.uploadingScreenshots = true;
        state.error = null;
      })
      .addCase(uploadLabPreferenceScreenshots.fulfilled, (state, action) => {
        state.uploadingScreenshots = false;
        const existingIds = new Set(state.screenshots.map((s) => s.fileId));
        for (const shot of action.payload) {
          if (!existingIds.has(shot.fileId)) {
            state.screenshots.push(shot);
          }
        }
      })
      .addCase(uploadLabPreferenceScreenshots.rejected, (state, action) => {
        state.uploadingScreenshots = false;
        state.error = action.payload as string;
      })
      .addCase(updateLabPreference.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(updateLabPreference.fulfilled, (state, action) => {
        state.updating = false;
        state.labPreferences = action.payload;
      })
      .addCase(updateLabPreference.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload as string;
      });
  },
});

export const { resetLabPreferenceState } = labDetailsSlice.actions;
export default labDetailsSlice.reducer;

export const labPreferenceScreenshotUrl = (token: string, fileId: string) =>
  `/auth/admin/labpreferences/${encodeURIComponent(token)}/screenshots/${encodeURIComponent(fileId)}`;
